import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { digilockerComplete } from "@lawyer/services/kycApi";

const POLL_MS = 2500;
const MAX_POLLS = 12;

export const digilockerErrorMessage = (e, fallback = "DigiLocker fetch failed") => {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object") return d.message || fallback;
  return d || fallback;
};

// Finishes a DigiLocker session when the lawyer lands back on /kyc?digilocker=<ref>.
export function useDigiLockerReturn(onCompleted) {
  const [state, setState] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // Capture once: clearing the query param below must not cancel the in-flight completion.
  const pendingRef = useRef(searchParams.get("digilocker"));
  const cancelledRef = useRef(false);

  useEffect(() => {
    const ref = pendingRef.current;
    if (!ref) return undefined;
    pendingRef.current = null;
    setSearchParams({}, { replace: true });
    let attempts = 0;
    setState({ status: "pending" });

    const tick = async () => {
      attempts += 1;
      try {
        const r = await digilockerComplete(ref);
        if (cancelledRef.current) return;
        if (r.status === "completed") {
          setState({ status: "completed", docType: r.doc_type, nameMatch: r.name_match_status });
          onCompleted?.(r);
          return;
        }
        if (attempts >= MAX_POLLS) {
          setState({ status: "error", message: "DigiLocker has not confirmed your consent yet. Please try again." });
          return;
        }
        setTimeout(tick, POLL_MS);
      } catch (e) {
        if (cancelledRef.current) return;
        setState({ status: "error", message: digilockerErrorMessage(e) });
      }
    };
    tick();
    return undefined;
  }, [onCompleted, setSearchParams]);

  // StrictMode runs effects mount→cleanup→mount: re-arm on every mount so the
  // simulated unmount can't permanently discard the real completion response.
  useEffect(() => {
    cancelledRef.current = false;
    return () => { cancelledRef.current = true; };
  }, []);

  return [state, () => setState(null)];
}
